import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput
} from '@mui/material';
import { useErrorMessage } from '@shared/hooks/use-error-message';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InfoTooltip from '@shared/components/info-tooltip/info-tooltip';
import useScreensStore from '../../store/screens.store';
import {
  DigitalSignageScreenReqModel,
  ScreensDialogFormProps,
  SourceType
} from '@modules/digital-signage/model/screens.model';
import {
  digitalSignageScreensTabsData,
  screensErrorMessages
} from '@modules/digital-signage/utils/digital-signage.utils';
import {
  trimFormValues,
  validateNotOnlySpaces
} from '@shared/utils/trim-form-values-utils';
import { FileTypeEnum } from '@models/media-library.model.ts';
import MediaLibraryComponent from '@components/media-library-component/media-library-component.tsx';
import TabsComponent from '@components/tabs/tabs.tsx';

const ScreensDialogForm: React.FC<ScreensDialogFormProps> = ({
  selectedItem,
  onSave
}) => {
  const {
    register,
    reset,
    handleSubmit,
    watch,
    formState: { errors, isValid }
  } = useForm<DigitalSignageScreenReqModel>({ mode: 'onTouched' });

  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const { isLoading } = useScreensStore();
  const [selectedMedia, setSelectedMedia] = useState<Record<string, any>>({});
  const [currentTab, setCurrentTab] = useState<string>('file');

  const appPathValue = watch('appPath');
  const mediaUrlValue = watch('mediaUrl');

  const isMediaSelected = !!selectedMedia.media?.path;
  const isAppPathFilled = !!appPathValue?.trim();
  const isUrlFilled = !!mediaUrlValue?.trim();

  const disabledTabs: Record<string, boolean> = {
    [SourceType.File]: isUrlFilled || isAppPathFilled,
    [SourceType.Url]: isMediaSelected || isAppPathFilled,
    [SourceType.AppPath]: isMediaSelected || isUrlFilled
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  const getServerFileType = (
    mime: string
  ): 'html' | 'image' | 'video' | 'audio' | 'other' => {
    if (!mime) return 'other';
    if (mime === 'text/html') return 'html';
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
    return 'other';
  };

  const handleSave = (data: DigitalSignageScreenReqModel) => {
    const trimmedData = trimFormValues(data);
    let mediaType: string | null = null;

    if (selectedMedia.media) {
      mediaType = getServerFileType(selectedMedia.media.mimetype);
    } else if (trimmedData.appPath || trimmedData.mediaUrl) {
      mediaType = 'html';
    }

    const body: DigitalSignageScreenReqModel = {
      title: trimmedData.title,
      appPath: trimmedData.appPath,
      media: selectedMedia.media?.path || null,
      mediaType,
      mediaUrl: trimmedData.mediaUrl || null,
      sourceType: currentTab
    };

    onSave(body);
  };

  useEffect(() => {
    if (selectedItem) {
      reset({
        title: selectedItem?.title || '',
        appPath: selectedItem?.appPath || '',
        mediaUrl: selectedItem?.mediaUrl || ''
      });

      if (selectedItem.sourceType) {
        setCurrentTab(selectedItem.sourceType);
      }
    }
  }, [selectedItem, reset]);

  return (
    <div>
      <div className="w-full mb-8">
        <TabsComponent
          value={currentTab}
          onChange={handleTabChange}
          tabs={digitalSignageScreensTabsData}
          disabled={disabledTabs}
        />
      </div>

      <form className="flex flex-col gap-1">
        <div className="flex flex-col gap-1 min-h-[280px] max-lg-xl:min-h-[262px]">
          <FormControl error={!!errors.title}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_title')}
              <InfoTooltip text="tooltip_info_digital_signage_screens_title" />
            </FormLabel>
            <OutlinedInput
              {...register('title', {
                required: true,
                validate: { notOnlySpaces: validateNotOnlySpaces }
              })}
              error={!!errors.title}
              placeholder={t('placeholder_digital_signage_screen_title')}
            />
            <FormHelperText>
              {errors?.title &&
                getErrorMessage({
                  errors,
                  fieldName: 'title',
                  errorMessages: screensErrorMessages
                })}
            </FormHelperText>
          </FormControl>

          {currentTab === SourceType.File && (
            <div
              className="media-wrapper mb-4 mt-1"
              style={{
                pointerEvents: isAppPathFilled ? 'none' : 'auto',
                opacity: isAppPathFilled ? 0.5 : 1
              }}
            >
              <div className="flex mb-1.5">{t('label_media')}</div>

              <MediaLibraryComponent
                mediaKey={'media'}
                width={240}
                height={120}
                title={'upload_all_content_text'}
                allowedTypes={[FileTypeEnum.All]}
                path={selectedItem?.media}
                onSelect={(media) =>
                  setSelectedMedia((prev) => ({ ...prev, media: media }))
                }
              />
            </div>
          )}

          {currentTab === SourceType.Url && (
            <FormControl className="w-full" error={!!errors.mediaUrl}>
              <FormLabel className="flex">
                {t('label_website_url')}
                <InfoTooltip text="tooltip_info_digital_signage_screens_website_url" />
              </FormLabel>
              <OutlinedInput
                {...register('mediaUrl', {
                  pattern:
                    /^(https?:\/\/)([a-z\d.-]+)(\.[a-z\d.-]+)+(:\d+)?(\/[^\s]*)?$/i
                })}
                placeholder={t(
                  'placeholder_digital_signage_screens_website_url'
                )}
              />
              <FormHelperText>
                {errors?.mediaUrl &&
                  getErrorMessage({
                    errors,
                    fieldName: 'mediaUrl',
                    errorMessages: screensErrorMessages
                  })}
              </FormHelperText>
            </FormControl>
          )}

          {currentTab === SourceType.AppPath && (
            <FormControl error={!!errors.appPath} disabled={isMediaSelected}>
              <FormLabel className="flex">
                {t('label_app_path')}
                <InfoTooltip text="tooltip_info_digital_signage_screens_app_path" />
              </FormLabel>
              <OutlinedInput
                {...register('appPath', {})}
                error={!!errors.appPath}
                disabled={isMediaSelected}
                placeholder={t('placeholder_digital_signage_screen_app_path')}
              />
              <FormHelperText>
                {errors?.appPath &&
                  getErrorMessage({
                    errors,
                    fieldName: 'appPath',
                    errorMessages: screensErrorMessages
                  })}
              </FormHelperText>
            </FormControl>
          )}
        </div>

        <div className="flex justify-end items-center gap-6 pt-4 pb-1 sticky bottom-0 bg-white">
          <Button
            disabled={!isValid}
            onClick={handleSubmit(handleSave)}
            variant="contained"
            loading={isLoading()}
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ScreensDialogForm;
